import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { QuestInstance, ChallengeEvent } from '../types/quest';
import { generateDailyQuests } from '../engine/quest-generator';

interface QuestState {
  dailyQuests: Record<string, QuestInstance[]>;
  challenges: ChallengeEvent[];
  getDailyQuests: (childId: string, date: string) => QuestInstance[];
  updateQuestProgress: (childId: string, date: string, questId: string, progress: number) => void;
  claimQuestReward: (childId: string, date: string, questId: string) => void;
  addChallenge: (challenge: ChallengeEvent) => void;
}

function questKey(childId: string, date: string): string {
  return `${date}-${childId}`;
}

export const useQuestStore = create<QuestState>()(
  persist(
    (set, get) => ({
      dailyQuests: {},
      challenges: [],

      getDailyQuests: (childId, date) => {
        const key = questKey(childId, date);
        const existing = get().dailyQuests[key];
        if (existing) return existing;

        const quests = generateDailyQuests(childId, date);
        set(state => ({
          dailyQuests: { ...state.dailyQuests, [key]: quests },
        }));
        return quests;
      },

      updateQuestProgress: (childId, date, questId, progress) => {
        const key = questKey(childId, date);
        set(state => {
          const quests = state.dailyQuests[key];
          if (!quests) return state;
          return {
            dailyQuests: {
              ...state.dailyQuests,
              [key]: quests.map(q => {
                if (q.id !== questId) return q;
                const completed = progress >= q.definition.target;
                return {
                  ...q,
                  progress,
                  completed,
                  ...(completed && !q.completed ? { completedAt: new Date().toISOString() } : {}),
                };
              }),
            },
          };
        });
      },

      claimQuestReward: (childId, date, questId) => {
        const key = questKey(childId, date);
        set(state => {
          const quests = state.dailyQuests[key];
          if (!quests) return state;
          return {
            dailyQuests: {
              ...state.dailyQuests,
              [key]: quests.map(q =>
                q.id === questId ? { ...q, claimed: true } : q
              ),
            },
          };
        });
      },

      addChallenge: (challenge) => {
        set(state => ({
          challenges: [...state.challenges, challenge],
        }));
      },
    }),
    { name: 'homeschool-quests', version: 0, migrate: (state) => state }
  )
);
